/**
 * PushOptInBanner — V1.7.0 Sprint 1.
 *
 * Slim slide-down banner shown to authenticated users who have NOT yet
 * granted Notification permission. Copy is contextual: instead of a
 * scary "Allow notifications?" prompt, we frame it as the value the
 * user gets — recruiter replies landing on their phone within seconds.
 *
 * Dismissal is remembered for 7 days per user so we don't nag.
 * Skips entirely on browsers that don't support Web Push.
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { BellRinging, X } from "@phosphor-icons/react";
import usePushSubscription from "@/hooks/usePushSubscription";

const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;   // 7 days

function dismissKey(userId) {
    return `push_optin_dismissed_${userId || "anon"}`;
}

export default function PushOptInBanner({ user, context = "chat" }) {
    const { supported, permission, subscribed, requestPermission } = usePushSubscription(user);
    const [busy, setBusy] = useState(false);
    const [hidden, setHidden] = useState(false);

    // Long-term dismiss check.
    const dismissed = useMemo(() => {
        try {
            const ts = Number(localStorage.getItem(dismissKey(user?.id)) || 0);
            return ts && Date.now() - ts < DISMISS_TTL_MS;
        } catch { return false; }
    }, [user?.id]);

    if (!supported) return null;
    if (!user?.id) return null;
    if (permission === "granted" && subscribed) return null;
    if (permission === "denied") return null;    // user said no — respect it
    if (dismissed || hidden) return null;

    const copyByContext = {
        chat: {
            headline: "Get recruiter replies instantly",
            body: "Turn on notifications so a recruiter's reply reaches you within seconds, not hours.",
            cta: "Turn on",
        },
        interview: {
            headline: "Never miss an interview reminder",
            body: "We'll ping you 30 min before every interview slot lands.",
            cta: "Turn on",
        },
        generic: {
            headline: "Get the app-like experience",
            body: "Turn on notifications for chat, interviews, and offer letters.",
            cta: "Turn on",
        },
        team_chat: {
            headline: "Never miss a team ping",
            body: "Turn on notifications so mentions and DMs reach you even when the app is closed.",
            cta: "Turn on",
        },
        daddytalk: {
            headline: "Reach candidates in real time",
            body: "Get instant notifications when a candidate messages you — even when your phone is locked.",
            cta: "Turn on",
        },
    };
    const copy = copyByContext[context] || copyByContext.generic;

    const onEnable = async () => {
        setBusy(true);
        const ok = await requestPermission();
        setBusy(false);
        if (ok) {
            toast.success("Notifications on. We'll ping you the moment a recruiter replies.");
        } else {
            toast.info("No worries — you can turn them on later from Settings.");
            setHidden(true);
        }
    };

    const onDismiss = () => {
        try { localStorage.setItem(dismissKey(user.id), String(Date.now())); } catch { /* ignore */ }
        setHidden(true);
    };

    return (
        <div
            data-testid="push-optin-banner"
            className="bg-gradient-to-r from-cobalt to-[#1E40AF] text-white px-4 py-3 flex items-center gap-3 shadow-md"
        >
            <BellRinging size={20} weight="fill" className="shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="font-black text-xs sm:text-sm leading-tight">
                    {copy.headline}
                </p>
                <p className="text-[11px] sm:text-xs opacity-90 mt-0.5 truncate sm:whitespace-normal">
                    {copy.body}
                </p>
            </div>
            <button
                type="button"
                data-testid="push-optin-enable"
                onClick={onEnable}
                disabled={busy}
                className="bg-white text-cobalt text-[10px] sm:text-xs font-black uppercase tracking-wider px-3 py-2 hover:bg-cream disabled:opacity-60 shrink-0"
            >
                {busy ? "…" : copy.cta}
            </button>
            <button
                type="button"
                data-testid="push-optin-dismiss"
                aria-label="Dismiss"
                onClick={onDismiss}
                className="text-white/70 hover:text-white p-1 shrink-0"
            >
                <X size={14} weight="bold" />
            </button>
        </div>
    );
}
